import React, { useState } from "react";
import {
  Check,
  Minus,
  ChevronDown,
  ChevronUp,
  Info,
  CheckSquare,
  Square,
} from "lucide-react";
import { offeringsService } from "../../../../services/api/settings";
// import { OfferingAddon } from "../../../../store/types/settings";
import formatPrice from "../../../../utility/formatPrice";
import formatText from "../../../../utility/formatText";

interface AddOnFeature {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  billingType: string;
  isActive: boolean;
  STARTER: boolean;
  GROWTH: boolean;
  SCALE: boolean;
  ENTERPRISE: boolean;
  activation: string;
  serviceType: string;
  pricing?: {
    monthly: number;
    yearly: number;
    activation: string;
  };
  planSpecificPricing?: {
    planId: string;
    price: number;
    currency?: string;
  }[];
  raw?: any;
}

interface DirectAddOnsProps {
  organizationId?: string;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  features?: any[];
  /** When true, AI Recruiter add-ons section is visible. Defaults to false. */
  showAiRecruiterAddOns?: boolean;
}

const DirectAddOns: React.FC<DirectAddOnsProps> = ({
  organizationId,
  selectedIds = [],
  onToggle,
  features: initialFeatures,
  showAiRecruiterAddOns = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [directAddOns, setDirectAddOns] = useState<AddOnFeature[]>([]);
  const [aiAddOns, setAiAddOns] = useState<AddOnFeature[]>([]);

  const normalizeAddon = (item: any): AddOnFeature => {
    const isDirect = item.serviceType === "DIRECT";
    const priceVal =
      typeof item.price === "string" ? parseFloat(item.price) : item.price || 0;

    return {
      id: item.id,
      name: item.name || item.displayName || "",
      displayName: item.displayName || item.name || "",
      description: item.description || "",
      price: priceVal,
      currency: item.currency || "NGN",
      category: item.category || "N/A",
      billingType: item.billingType || "one-off",
      isActive: item.isActive !== false,
      STARTER: isDirect ? !!item.start : true,
      GROWTH: isDirect ? !!item.pro : true,
      SCALE: isDirect ? !!item.enterprise : true,
      ENTERPRISE: isDirect ? !!item.vip : true,
      activation: "Instant",
      serviceType: item.serviceType || "DIRECT",
      pricing: {
        monthly: priceVal,
        yearly: priceVal * 11, // Standard 11 months billing for yearly packages
        activation: "Instant",
      },
      planSpecificPricing:
        item.raw?.planSpecificPricing || item.planSpecificPricing,
      raw: item,
    };
  };

  React.useEffect(() => {
    const processFeatures = (items: any[]) => {
      const normalized = items.map(normalizeAddon);
      setDirectAddOns(
        normalized.filter((item) => item.serviceType === "DIRECT"),
      );
      setAiAddOns(
        normalized.filter((item) => item.serviceType === "RECRUITER"),
      );
    };

    if (initialFeatures && initialFeatures.length > 0) {
      processFeatures(initialFeatures);
    } else {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await offeringsService.getAll();
          if (response.status === "success" && response.data) {
            const allAddons = [
              ...(response.data.DIRECT?.addons || []),
              ...(response.data.RECRUITER?.addons || []),
            ];
            processFeatures(allAddons);
          }
        } catch (error) {
          console.error("Failed to fetch offerings for DirectAddOns", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [initialFeatures]);

  const renderStatus = (status: boolean) => {
    return status ? (
      <Check className="w-5 h-5 text-gray-800 mx-auto" />
    ) : (
      <Minus className="w-5 h-5 text-gray-400 mx-auto" />
    );
  };

  const getTierPrice = (
    feature: AddOnFeature,
    tier: "STARTER" | "GROWTH" | "SCALE" | "ENTERPRISE",
  ) => {
    const name = (feature.displayName || feature.name || "").toLowerCase();
    const isDirect = feature.serviceType === "DIRECT";

    // 1. Resolve backend planId based on serviceType
    let planId = "";
    if (isDirect) {
      if (tier === "STARTER") planId = "STARTER";
      else if (tier === "GROWTH") planId = "GROWTH";
      else if (tier === "SCALE") planId = "SCALE";
      else if (tier === "ENTERPRISE") planId = "ENTERPRISE";
    } else {
      // RECRUITER (AI Recruiter)
      if (tier === "STARTER") planId = "STARTER";
      else if (tier === "GROWTH") planId = "GROWTH";
      else if (tier === "SCALE") planId = "SCALE";
      else if (tier === "ENTERPRISE") planId = "ENTERPRISE";
    }

    // 2. Look for plan-specific price
    const pPricing = feature.planSpecificPricing?.find(
      (p) => p.planId?.toUpperCase() === planId,
    );

    // Helper to format price with zero decimals
    const formatIntPrice = (p: number) => {
      return new Intl.NumberFormat("en-NG", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(p);
    };

    // 3. Render prices according to sample layout or fallback
    if (isDirect) {
      // Direct pricing
      if (name.includes("credits") || name.includes("credits top-up")) {
        if (tier === "STARTER") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "150,000";
          return `${val}`;
        }
        if (tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "150,000";
          return `${val}`;
        }
        if (tier === "SCALE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "120,000";
          return `${val}`;
        }
        if (tier === "ENTERPRISE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "100,000";
          return `${val}`;
        }
      }

      if (name.includes("migration")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "800,000";
          return `${val}`;
        }
        if (tier === "SCALE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "600,000";
          return `${val}`;
        }
        return "Custom";
      }

      if (
        name.includes("cv upload") ||
        name.includes("cv upload boost") ||
        name.includes("bulk cv")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "300,000";
          return `${val}`;
        }
        return "Included";
      }

      if (
        name.includes("sso") ||
        name.includes("saml") ||
        name.includes("sso / saml")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "500,000";
          return `${val}`;
        }
        return "Included";
      }

      if (
        name.includes("build") ||
        name.includes("custom / enterprise") ||
        name.includes("custom build")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") return "—";
        return "Contact Sales";
      }
    } else {
      // AI Recruiter pricing
      if (name.includes("interview") || name.includes("top-up")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "400,000";
          return `${val} (10 sessions)`;
        }
        if (tier === "SCALE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "350,000";
          return `${val} (12 sessions)`;
        }
        return "Custom";
      }

      if (name.includes("video") || name.includes("storage")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "500,000";
          return `${val}`;
        }
        return "Included";
      }

      if (
        name.includes("branding") ||
        name.includes("white-label") ||
        name.includes("white label")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "1,500,000";
          return `${val}`;
        }
        return "Included";
      }

      if (name.includes("tuning") || name.includes("model")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          return pPricing && pPricing.price > 0
            ? `${formatIntPrice(pPricing.price)}`
            : "Per quote";
        }
        if (tier === "SCALE") {
          return "1 included";
        }
        return "Custom";
      }

      if (
        name.includes("build") ||
        name.includes("custom / enterprise") ||
        name.includes("custom build")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") return "—";
        return "Contact Sales";
      }
    }

    // Fallback to planSpecificPricing format if present
    if (pPricing) {
      return `${formatIntPrice(pPricing.price)}`;
    }

    return "Custom";
  };

  const isSelectable = (f: any) => {
    return f.isActive !== false;
  };

  return (
    <section id="direct-addons" className="pb-2 px-3 lg:px-8">
      <div className="max-w-[950px] mx-auto">
        <div className="bg-white rounded-[15px] shadow-sm border border-primary overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors rounded-[15px]"
          >
            <h3 className="text-[15px] md:text-[17px] font-medium text-[#212121]">
              Add-Ons (Direct{showAiRecruiterAddOns ? " & AI Recruiter" : ""})
            </h3>
            {isOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-500" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-500" />
            )}
          </button>

          {isOpen && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-3 w-[50px] text-center">
                      {onToggle && (
                        <div
                          className="flex items-center justify-center cursor-pointer"
                          onClick={() => {
                            const selectableFeatures = directAddOns.filter(
                              (f) => isSelectable(f),
                            );
                            const selectableAddOns = showAiRecruiterAddOns
                              ? aiAddOns.filter((a) => isSelectable(a))
                              : [];
                            const allIds = [
                              ...selectableFeatures.map((f) => f.id),
                              ...selectableAddOns.map((f) => f.id),
                            ];
                            const allSelected =
                              allIds.length > 0 &&
                              allIds.every((id) => selectedIds.includes(id));
                            if (allSelected) {
                              // Deselect all
                              allIds.forEach((id) => {
                                if (selectedIds.includes(id)) onToggle(id);
                              });
                            } else {
                              // Select all
                              allIds.forEach((id) => {
                                if (!selectedIds.includes(id)) onToggle(id);
                              });
                            }
                          }}
                        >
                          {(() => {
                            const selectableFeatures = directAddOns.filter(
                              (f) => isSelectable(f),
                            );
                            const selectableAddOns = showAiRecruiterAddOns
                              ? aiAddOns.filter((a) => isSelectable(a))
                              : [];
                            const allIds = [
                              ...selectableFeatures.map((f) => f.id),
                              ...selectableAddOns.map((f) => f.id),
                            ];
                            const allSelected =
                              allIds.length > 0 &&
                              allIds.every((id) => selectedIds.includes(id));
                            return allSelected ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                            );
                          })()}
                        </div>
                      )}
                    </th>
                    <th className="text-left px-6 py-3 text-[14px] font-medium text-[#212121] w-[35%]">
                      Services
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Starter
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Growth
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Scale
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {directAddOns.length > 0 && (
                    <tr className="bg-gray-50 border-y border-gray-200 text-left">
                      <td
                        colSpan={6}
                        className="px-6 py-3 text-[15px] font-bold text-gray-900 text-left"
                      >
                        Direct Add-Ons
                      </td>
                    </tr>
                  )}
                  {directAddOns.map((feature) => {
                    const isSelected = selectedIds.includes(feature.id);
                    const canSelect = isSelectable(feature);
                    return (
                      <tr
                        key={feature.id}
                        className={`hover:bg-gray-50 transition-colors ${
                          isSelected ? "bg-blue-50/50" : ""
                        }`}
                        onClick={() =>
                          canSelect && onToggle && onToggle(feature.id)
                        }
                      >
                        <td className="px-3 py-3 text-center cursor-pointer">
                          {onToggle && canSelect && (
                            <div className="flex items-center justify-center">
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-[14px] text-[#616161]">
                          <div className="flex items-center gap-1 group relative">
                            {feature.name}
                            {feature.pricing && (
                              <>
                                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                                <div className="invisible group-hover:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                    <div className="text-gray-500 text-xs">
                                      Monthly
                                    </div>
                                    <div className="text-gray-900 font-normal text-right">
                                      {formatPrice(feature.pricing.monthly)}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      Annually
                                    </div>
                                    <div className="text-gray-900 font-normal text-right">
                                      {formatPrice(feature.pricing.yearly)}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      Activation
                                    </div>
                                    <div className="text-gray-900 font-normal text-right">
                                      {feature.pricing.activation}
                                    </div>
                                  </div>
                                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-8 border-transparent border-r-white"></div>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "STARTER")}
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "GROWTH")}
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "SCALE")}
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "ENTERPRISE")}
                        </td>
                      </tr>
                    );
                  })}

                  {showAiRecruiterAddOns && aiAddOns.length > 0 && (
                    <tr className="bg-gray-50 border-y border-gray-200 text-left">
                      <td
                        colSpan={6}
                        className="px-6 py-3 text-[15px] font-bold text-gray-900 text-left"
                      >
                        AI Recruiter Add-Ons
                      </td>
                    </tr>
                  )}
                  {showAiRecruiterAddOns && aiAddOns.map((feature) => {
                    const isSelected = selectedIds.includes(feature.id);
                    const canSelect = isSelectable(feature);
                    return (
                      <tr
                        key={feature.id}
                        className={`hover:bg-gray-50 transition-colors ${
                          isSelected ? "bg-blue-50/50" : ""
                        }`}
                        onClick={() =>
                          canSelect && onToggle && onToggle(feature.id)
                        }
                      >
                        <td className="px-3 py-3 text-center cursor-pointer">
                          {onToggle && canSelect && (
                            <div className="flex items-center justify-center">
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-[14px] text-[#616161]">
                          <div className="flex items-center gap-1 group relative">
                            {feature.displayName || feature.name}
                            {feature.price > 0 && (
                              <>
                                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                                <div className="invisible group-hover:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                    <div className="text-gray-500 text-xs">
                                      Price
                                    </div>
                                    <div className="text-gray-900 font-normal text-right">
                                      {formatPrice(
                                        feature.price,
                                        feature.currency,
                                      )}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      Billing Type
                                    </div>
                                    <div className="text-gray-900 font-normal text-right">
                                      {formatText(feature.billingType)}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      Activation
                                    </div>
                                    <div className="text-gray-900 font-normal text-right">
                                      {feature.activation}
                                    </div>
                                  </div>
                                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-8 border-transparent border-r-white"></div>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "STARTER")}
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "GROWTH")}
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "SCALE")}
                        </td>
                        <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                          {getTierPrice(feature, "ENTERPRISE")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DirectAddOns;
